/**
 * @file 参考書ルートを「科目 × 難易度」の棒に組み替える純関数を置く。
 *
 * @what: 同じ受験の対象のルート (理系編・文系編) を1枚に重ね、本と志望校に出てくる偏差値の値を
 *   行に、科目を列にした表の材料を作る。目標の偏差値を与えると、科目ごとに「そこまで届く本が無い
 *   切り口」を返す。
 * @why: 難易度は段を別に定義せず、出典に出た偏差値の数値をそのまま行にする (設計
 *   plan/docs/study_textbook_route_grid_design.md §3.2)。切り口 (区分) は列にせずラベルと抜けの点検で使う。
 * @failure: 描画専用の純関数で、偏差値を持たないレベルだけの本は「目安なし」の行にまとめて落とさない。
 */

import type {
  StudyTextbookRoute,
  StudyTextbookRouteBook,
  StudyTextbookRouteCareer,
  StudyTextbookRouteFlow,
  StudyTextbookRouteLevel,
  StudyTextbookRouteScaleMark,
  StudyTextbookRouteSchool,
  StudyTextbookRouteSubject,
  StudyTextbookRouteTask,
  StudyTextbookRouteTrack,
} from "../api/textbookRoutes";

/** 表に載せる本1冊と、その本がどの科目・切り口に属するか。 */
export interface GridBook {
  /** 同一視のキー (ISBN、無ければ書名)。 */
  key: string;
  book: StudyTextbookRouteBook;
  subjectId: string;
  trackId: string;
  trackLabel: string;
  /** 重ねたルートのうち、この本を載せているルートの題名 (理系編・文系編)。 */
  routeTitles: string[];
}

export interface GridTrack {
  id: string;
  label: string;
  flow: StudyTextbookRouteFlow;
  /** 試験日の何か月前から始めるか。無ければ null で、初学者のスケジュールでは週の時間から逆算する。 */
  startMonthsBeforeExam: number | null;
  startLabel: string | null;
}

/** 表に載せる課題1件と、それがどの科目・切り口に属するか。 */
export interface GridTask {
  /** 同一視のキー (科目と切り口と課題の id をつないだもの)。 */
  key: string;
  task: StudyTextbookRouteTask;
  subjectId: string;
  trackId: string;
  trackLabel: string;
}

export interface GridSubject {
  id: string;
  label: string;
  /** 切り口 (id と表示名と進め方)。同じ id は重ねたルートで1つにする。 */
  tracks: GridTrack[];
  books: GridBook[];
  /** 課題で、技能のカリキュラムだけが持つ。 */
  tasks: GridTask[];
}

/** 同じ受験の対象のルートを1枚に重ねたもの。 */
export interface MergedTarget {
  target: string;
  scale?: string;
  /** 目盛りの定義で、空なら数値をそのまま目盛りにする。 */
  scaleMarks: StudyTextbookRouteScaleMark[];
  /** 目盛りの刻みで、null なら偏差値の5刻みを使う。 */
  scaleStep: number | null;
  /** 試験を受ける学年の通し番号 (小6=6、高3=12)。重ねたルートの最初のものを使う。 */
  examGrade: number | null;
  /** 試験日の月日 ("02-01")。 */
  examMonthDay: string | null;
  routeTitles: string[];
  schools: StudyTextbookRouteSchool[];
  /** 試験の先の働き方で、資格のルートだけが持つ。 */
  careers: StudyTextbookRouteCareer[];
  subjects: GridSubject[];
}

/**
 * 文の中に差し込む物差しの短い名前を返す。
 *
 * @what: 目盛りの定義を持つルートは「水準」、持たないルート (受験) は「偏差値」を返す。
 * @why: route.scale は "全統記述模試の偏差値" のような説明の名前で、文の中に差し込むと長すぎる。
 *   見出しや列の名前には scale をそのまま使い、文の中ではこの短い名前を使う。
 */
export function scaleNoun(m: MergedTarget): string {
  return m.scaleMarks.length > 0 ? "水準" : "偏差値";
}

/**
 * 終わりの日の呼び名を返す。
 *
 * @what: 目盛りの定義を持つルート (教養・技能・資格) は「期限」、持たないルート (受験) は「試験日」を返す。
 * @why: 同じ欄を受験では試験日、それ以外では期限と呼ぶ。欄の名前だけでなく、案内や週の分の文にも同じ
 *   呼び名を差し込む。※なぜなら期限を入れた人に「試験日を入れてください」と出ると、入れたものが効いて
 *   いないように見えるためである (代表の指摘、2026-09-13)。
 */
export function deadlineNoun(m: MergedTarget): string {
  return m.scaleMarks.length > 0 ? "期限" : "試験日";
}

/** 目盛りの値を画面に出す文字にする (目盛りの定義があればその名前、無ければ数値)。 */
export function scaleMarkLabel(m: MergedTarget, value: number): string {
  const mark = m.scaleMarks.find((x) => x.value === value);
  return mark ? mark.label : String(value);
}

/** 目盛りの出どころを触れたときの説明にする (無ければ付けない)。 */
export function scaleMarkNote(m: MergedTarget, value: number): string | undefined {
  const mark = m.scaleMarks.find((x) => x.value === value);
  return mark?.note && mark.note.length > 0 ? mark.note : undefined;
}

/** レベルが偏差値の数値を持つかを返す。 */
function isRated(l: StudyTextbookRouteLevel): boolean {
  return typeof l.deviation === "number" && l.deviation > 0;
}

/** 本の水準 (使い始める偏差値の目安と使い続ける上限) と出どころを表す。 */
export interface BookDifficulty {
  from: number;
  to: number;
  source: string | null;
}

/** 本の水準を返す。difficulty が無い本 (古いルート) は null で、呼び手は到達の目安 (levels) で並べる。 */
export function bookDifficulty(b: StudyTextbookRouteBook): BookDifficulty | null {
  if (typeof b.difficulty !== "number" || b.difficulty <= 0) return null;
  const to =
    typeof b.difficulty_to === "number" && b.difficulty_to > b.difficulty
      ? b.difficulty_to
      : b.difficulty;
  return { from: b.difficulty, to, source: b.difficulty_source ? b.difficulty_source : null };
}

export function bookKey(b: StudyTextbookRouteBook): string {
  return b.isbn ? `isbn:${b.isbn}` : `title:${b.title}`;
}

/**
 * 同じ target のルートを1枚に重ねる。
 *
 * @what: 科目は id で、切り口は科目の中の id で、本はキー (ISBN か書名) で同一視する。志望校は名前で
 *   重ね、先に出たものを残す。並びは受け取った順 (ルートの順 → 科目の順 → 本の順)。
 * @why: 理系編と文系編は英語・数学が同じ本なので、そのまま並べると表に同じ本が2度出る。
 */
export function mergeRoutesByTarget(routes: readonly StudyTextbookRoute[]): MergedTarget[] {
  const out: MergedTarget[] = [];
  for (const r of routes) {
    let m = out.find((x) => x.target === r.target);
    if (!m) {
      m = {
        target: r.target,
        scaleMarks: [],
        scaleStep: null,
        routeTitles: [],
        schools: [],
        careers: [],
        subjects: [],
        examGrade: null,
        examMonthDay: null,
      };
      if (r.scale) m.scale = r.scale;
      out.push(m);
    }
    if (!m.scale && r.scale) m.scale = r.scale;
    // 目盛りの定義と刻みは、同じ対象の最初に見つけたルートのものを使う。※なぜなら理系編と文系編の
    // ように重ねるルートは同じ物差しを共有しており、後から違う定義が来ることを想定しないためである。
    if (m.scaleMarks.length === 0 && (r.scale_marks ?? []).length > 0)
      m.scaleMarks = [...(r.scale_marks ?? [])].sort((a, b) => a.value - b.value);
    if (m.scaleStep === null && typeof r.scale_step === "number" && r.scale_step > 0)
      m.scaleStep = r.scale_step;
    if (m.examGrade === null && typeof r.exam_grade === "number" && r.exam_grade > 0)
      m.examGrade = r.exam_grade;
    if (m.examMonthDay === null && r.exam_month_day) m.examMonthDay = r.exam_month_day;
    m.routeTitles.push(r.title);
    for (const sc of r.schools ?? []) {
      if (!m.schools.some((x) => x.name === sc.name)) m.schools.push(sc);
    }
    for (const c of r.careers ?? []) {
      if (!m.careers.some((x) => x.name === c.name)) m.careers.push(c);
    }
    for (const s of r.subjects) mergeSubject(m, s, r.title);
  }
  return out;
}

function mergeSubject(m: MergedTarget, s: StudyTextbookRouteSubject, routeTitle: string): void {
  let gs = m.subjects.find((x) => x.id === s.id);
  if (!gs) {
    gs = { id: s.id, label: s.label, tracks: [], books: [], tasks: [] };
    m.subjects.push(gs);
  }
  for (const t of s.tracks) mergeTrack(gs, t, routeTitle);
}

function mergeTrack(gs: GridSubject, t: StudyTextbookRouteTrack, routeTitle: string): void {
  if (!gs.tracks.some((x) => x.id === t.id))
    gs.tracks.push({
      id: t.id,
      label: t.label,
      flow: t.flow ?? "parallel",
      startMonthsBeforeExam:
        typeof t.start_months_before_exam === "number" && t.start_months_before_exam > 0
          ? t.start_months_before_exam
          : null,
      startLabel: t.start_label && t.start_label.length > 0 ? t.start_label : null,
    });
  for (const k of t.tasks ?? []) {
    const key = `${gs.id}/${t.id}/${k.id}`;
    if (gs.tasks.some((x) => x.key === key)) continue;
    gs.tasks.push({ key, task: k, subjectId: gs.id, trackId: t.id, trackLabel: t.label });
  }
  for (const b of t.books) {
    const key = bookKey(b);
    const existing = gs.books.find((x) => x.key === key);
    if (existing) {
      if (!existing.routeTitles.includes(routeTitle)) existing.routeTitles.push(routeTitle);
      continue;
    }
    gs.books.push({
      key,
      book: b,
      subjectId: gs.id,
      trackId: t.id,
      trackLabel: t.label,
      routeTitles: [routeTitle],
    });
  }
}

/** 目盛りの1つ。major は名前や数字を出す目盛りである。 */
export interface RangeTick {
  value: number;
  major: boolean;
  /** 目盛りの名前で、ルートが目盛りの定義を持つときだけ入る。 */
  label?: string;
}

/** 棒を置く物差しを表す。ルートに出てくる値から5刻みで決める。 */
export interface RangeAxis {
  min: number;
  max: number;
  ticks: RangeTick[];
}

/**
 * 物差しを作る。
 *
 * @what: ルートが目盛りの定義 (scaleMarks) を持つときは、その位置をそのまま目盛りにし、両端に
 *   半目盛りぶんの余白を置く。持たないときは、本の水準 (使い始める・使い終える)、出典の到達の目安、
 *   目標の印に出てくる値の最小と最大を5刻みに丸めて範囲にし、2.5刻みの目盛りを返す。数値が1つも
 *   無ければ null を返す。
 * @why: ルートごとに扱う範囲が違う (大学入試は45〜75、中学入試は45〜70)。固定の範囲にすると片方で
 *   棒が端に寄る。資格と教養のカリキュラムは目盛りが3〜4個しかないので、5刻みに丸めると軸が
 *   0〜5の1目盛りに潰れて棒が読めなくなる (設計 §3.4)。
 */
export function rangeAxis(m: MergedTarget): RangeAxis | null {
  const values: number[] = [];
  for (const s of m.subjects) {
    for (const gb of s.books) {
      if (gb.book.role === "supplement") continue;
      const diff = bookDifficulty(gb.book);
      if (diff !== null) {
        values.push(diff.from, diff.to);
      }
      for (const l of gb.book.levels) if (isRated(l)) values.push(l.deviation as number);
    }
  }
  for (const s of m.subjects) {
    for (const gt of s.tasks) {
      const bar = taskBar(gt);
      if (bar !== null) values.push(bar.from, bar.to);
    }
  }
  for (const sc of m.schools) values.push(sc.deviation);
  if (m.scaleMarks.length > 0) return markedRangeAxis(m.scaleMarks, values);
  if (values.length === 0) return null;
  const min = Math.floor(Math.min(...values) / 5) * 5;
  const max = Math.ceil(Math.max(...values) / 5) * 5;
  if (max <= min) return null;
  const ticks: RangeTick[] = [];
  for (let v = min; v <= max + 0.01; v += 2.5)
    ticks.push({ value: v, major: Math.abs(v / 5 - Math.round(v / 5)) < 0.01 });
  return { min, max, ticks };
}

/**
 * 目盛りの定義から物差しを作る。
 *
 * @what: 目盛りの値をそのまま目盛りの位置にし、両端に半目盛りぶんの余白を置く。本の値が目盛りの
 *   外にあるときは、その値まで軸を広げる。目盛りが1つだけのときは前後に0.5ずつ広げる。
 * @why: 余白を置かないと、最小の段の本の棒が左端に張り付いて幅を持てない。軸を本の値まで広げるのは、
 *   ※なぜなら本の難易度は段の位置ちょうどではなく前後に散らばり、軸からはみ出した棒は端で
 *   潰れて読めなくなるためである。
 */
function markedRangeAxis(
  marks: readonly StudyTextbookRouteScaleMark[],
  values: readonly number[],
): RangeAxis | null {
  const sorted = [...marks].sort((a, b) => a.value - b.value);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (!first || !last) return null;
  const gap = sorted.length > 1 ? (last.value - first.value) / (sorted.length - 1) : 1;
  const pad = gap / 2;
  const min = Math.min(first.value - pad, ...values);
  const max = Math.max(last.value + pad, ...values);
  if (max <= min) return null;
  return {
    min,
    max,
    ticks: sorted.map((mk) => ({ value: mk.value, major: true, label: mk.label })),
  };
}

/** 物差しの上の位置を百分率で返す (0が左端、100が右端)。 */
export function axisPercent(axis: RangeAxis, value: number): number {
  const p = ((value - axis.min) / (axis.max - axis.min)) * 100;
  return Math.min(Math.max(p, 0), 100);
}

/** 本1冊の棒を表す。 */
export interface RangeBar {
  book: GridBook;
  /** 使い始める偏差値で、水準の無い古いルートでは到達の目安の最小を置く。 */
  from: number;
  /** 使い終える偏差値で、範囲を持たない本は from と同じになる。 */
  to: number;
  /**
   * 仕上げたときに届くとされる偏差値 (出典の習得レベルの最大)。使う範囲の中に収まるときは null で、
   * 薄い延長を出さない。
   */
  reach: number | null;
  /** 到達の段階 (基準の文と偏差値)。触れたときに出す。 */
  steps: { deviation: number; label: string }[];
  /** 水準の出どころ。水準を持たない本では null になる。 */
  source: string | null;
  /** 水準を持たず、到達の目安から範囲を置いた本かどうか。 */
  fromReach: boolean;
}

/**
 * 本1冊の棒を返す。数値をまったく持たない本 (「共通テスト80%」だけの本など) は null を返す。
 *
 * @what: 水準のある本は使い始める値と使い終える値をそのまま使い、出典の到達の目安が使い終える値より
 *   先にあればそれを reach に入れる。水準の無い古いルートは到達の目安の最小と最大を範囲にする。
 * @why: 1冊の本を1本の棒にすると、表のときのように同じ本が何行にも現れなくなる。使う範囲と、仕上げた
 *   ときに届くとされる範囲は出どころが違うので、濃さを変えて重ねる (設計 §6.13)。
 */
export function rangeBar(gb: GridBook): RangeBar | null {
  const b = gb.book;
  const rated = b.levels.filter(isRated).map((l) => l.deviation as number);
  const steps = b.levels
    .filter(isRated)
    .map((l) => ({ deviation: l.deviation as number, label: l.label }))
    .sort((x, y) => x.deviation - y.deviation);
  const diff = bookDifficulty(b);
  if (diff !== null) {
    const max = rated.length > 0 ? Math.max(...rated) : null;
    return {
      book: gb,
      from: diff.from,
      to: diff.to,
      reach: max !== null && max > diff.to ? max : null,
      steps,
      source: diff.source,
      fromReach: false,
    };
  }
  if (rated.length === 0) return null;
  return {
    book: gb,
    from: Math.min(...rated),
    to: Math.max(...rated),
    reach: null,
    steps,
    source: null,
    fromReach: true,
  };
}

/** 科目の中の切り口1つと、その切り口の本の棒を表す。 */
export interface RangeTrackRow {
  track: GridTrack;
  bars: RangeBar[];
  /** 偏差値の数値を持たない本 (「共通テスト80%」だけの本など)。棒にできないので下に名前だけ出す。 */
  unrated: GridBook[];
  /** 課題の棒で、技能のカリキュラムだけが持つ。本の棒の下に並べる。 */
  taskBars: TaskBar[];
}

/** 課題1件の棒を表す。 */
export interface TaskBar {
  task: GridTask;
  /** 使い始める水準が入る。 */
  from: number;
  /** 使い終える水準が入る。 */
  to: number;
}

/**
 * 課題を棒にする。
 *
 * @what: 幅を持つ課題だけを返す。水準を持たない課題は null になる。
 * @why: 課題も本と同じ物差しの上に置くので、同じ軸で並べて読めるようにする。
 */
export function taskBar(gt: GridTask): TaskBar | null {
  const k = gt.task;
  if (typeof k.difficulty !== "number" || k.difficulty <= 0) return null;
  const to =
    typeof k.difficulty_to === "number" && k.difficulty_to > k.difficulty
      ? k.difficulty_to
      : k.difficulty;
  return { task: gt, from: k.difficulty, to };
}

/**
 * 科目を切り口ごとの行にまとめる。
 *
 * @what: 切り口の並びは出典のままで、その中の本の並びも出典のままにする。補助教材は載せない
 *   (supplementBooks で数える)。
 * @why: 出典の並びには意味がある (推奨が先、基礎から応用へ) ので、画面で並べ替えない。
 */
export function rangeRows(subject: GridSubject): RangeTrackRow[] {
  return subject.tracks.map((track) => {
    const bars: RangeBar[] = [];
    const unrated: GridBook[] = [];
    for (const gb of subject.books) {
      if (gb.trackId !== track.id || gb.book.role === "supplement") continue;
      const bar = rangeBar(gb);
      if (bar) bars.push(bar);
      else unrated.push(gb);
    }
    const taskBars: TaskBar[] = [];
    for (const gt of subject.tasks) {
      if (gt.trackId !== track.id) continue;
      const bar = taskBar(gt);
      if (bar) taskBars.push(bar);
    }
    return { track, bars, unrated, taskBars };
  });
}

/**
 * 本を使う上限の偏差値を返す。水準 (difficulty) があればその使い続ける上限、無ければ到達の目安
 * (levels の最大) で、どちらも無ければ null になる。抜けの点検とスケジュールの選び方に使う。
 */
export function bookCeiling(b: StudyTextbookRouteBook): number | null {
  const diff = bookDifficulty(b);
  return diff !== null ? diff.to : bookReach(b);
}

/** 本の到達の目安 (出典の習得レベルの数値の最大) を返す。数値が無ければ null になる。 */
export function bookReach(b: StudyTextbookRouteBook): number | null {
  let max: number | null = null;
  for (const l of b.levels) {
    if (typeof l.deviation === "number" && l.deviation > 0 && (max === null || l.deviation > max))
      max = l.deviation;
  }
  return max;
}

export interface CoverageGap {
  subjectId: string;
  subjectLabel: string;
  /** 目標まで届く本が無い切り口を並べる。 */
  tracks: { id: string; label: string }[];
}

/**
 * 抜けの点検を行う。
 *
 * @what: 目標の偏差値以上まで使う本 (水準の上限、無ければ到達の目安) が1冊も無い切り口を、科目ごとに
 *   返す。抜けの無い科目は返さない。
 * @why: 切り口を列にしないぶん、網羅性はここで確かめる (設計 §2)。数値を持たない本は
 *   「届く」と数えない (届くかどうかが分からないため)。
 */
export function coverageGaps(m: MergedTarget, targetDeviation: number): CoverageGap[] {
  const gaps: CoverageGap[] = [];
  for (const s of m.subjects) {
    const missing = s.tracks.filter(
      (t) =>
        !s.books.some((gb) => {
          if (gb.trackId !== t.id) return false;
          const reach = bookCeiling(gb.book);
          return reach !== null && reach >= targetDeviation;
        }),
    );
    if (missing.length > 0) gaps.push({ subjectId: s.id, subjectLabel: s.label, tracks: missing });
  }
  return gaps;
}

/** 表に載せない補助教材 (出典が用意する市販でない教材)。表の下に冊数を出すために数える。 */
export function supplementBooks(m: MergedTarget): GridBook[] {
  const out: GridBook[] = [];
  for (const s of m.subjects)
    for (const gb of s.books) if (gb.book.role === "supplement") out.push(gb);
  return out;
}
